package com.dotdrives.web.pages.givers

import com.dotdrives.controller.*
import com.dotdrives.model.ReminderSubjectOption
import com.dotdrives.web.AppRoute
import com.dotdrives.web.entityRouteHash
import com.dotdrives.web.pages.nextsteps.NewReminderDialog
import com.dotdrives.web.pages.nextsteps.NextStepsStyles
import kotlinx.browser.window
import kotlin.time.Clock
import com.dotdrives.controller.GiverDetailController
import com.dotdrives.controller.GiverDetailUiState
import com.dotdrives.controller.GiversController
import com.dotdrives.controller.GiversUiState
import com.dotdrives.controller.NewGiverController
import com.dotdrives.controller.NewGiverUiState
import com.dotdrives.design.DotTone
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotBanner
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.useEffect
import react.useState
import web.cssom.ClassName

external interface GiversPageProps : Props {
    var orgId: String
    var initialGiverId: String?
    var canFinance: Boolean
    var experienceController: GiverExperienceController
    var experienceState: GiverExperienceUiState
    var reminderController: NewReminderController
    var reminderState: NewReminderUiState
    var giversController: GiversController
    var giversState: GiversUiState
    var detailController: GiverDetailController
    var detailState: GiverDetailUiState
    var newGiverController: NewGiverController
    var newGiverState: NewGiverUiState
    var currentUserId: String
    var currentUserName: String
    var members: List<OrgMember>
    var canWrite: Boolean
    var dialogOpen: Boolean
    var importOpen: Boolean
    var onOpenDialog: () -> Unit
    var onCloseDialog: () -> Unit
    var onOpenImport: () -> Unit
    var onCloseImport: () -> Unit
}

val GiversPage = FC<GiversPageProps> { props ->
    val (selected, setSelected) = useState<GiverSummary?>(null)
    val (reminderOpen, setReminderOpen) = useState(false)
    val (importedCount, setImportedCount) = useState<Int?>(null)

    useEffect(props.initialGiverId) {
        val id = props.initialGiverId
        if (id == null) { setSelected(null); props.experienceController.close() }
        else {
            setSelected(GiverSummary(id, "Loading giver…", "individual", null, null, "unknown", emptyList()))
            props.detailController.open(id); props.experienceController.open(id)
        }
    }
    useEffect(props.reminderState.savedReminder?.id) { if (props.reminderState.savedReminder != null) setReminderOpen(false) }
    useEffect(props.newGiverState.savedGiver?.id) {
        val saved = props.newGiverState.savedGiver
        if (saved != null) {
            val summary = GiverSummary(
                id = saved.id,
                displayName = saved.displayName,
                giverType = saved.giverType,
                stewardUserId = saved.stewardUserId,
                stewardName = props.members
                    .firstOrNull { it.userId == saved.stewardUserId }
                    ?.displayName,
                temperature = saved.temperature,
                tags = saved.tags,
            )
            if (props.newGiverState.createDriveAfterSave) {
                window.location.hash = entityRouteHash(AppRoute.Drives, props.orgId, saved.id, true)
            } else {
                window.location.hash = entityRouteHash(AppRoute.Givers, props.orgId, saved.id)
                setSelected(summary)
                props.detailController.open(saved.id)
                props.experienceController.open(saved.id)
            }
            props.giversController.load()
            props.onCloseDialog()
            props.newGiverController.startCreate()
        }
    }

    GiversStyles()
    div {
        className = ClassName("givers-page")
        importedCount?.let { count ->
            DotBanner {
                message = "$count giver${if (count == 1) "" else "s"} imported successfully."
                tone = DotTone.Success
                className = "givers-inline-banner"
            }
        }
        GiversTable {
            state = props.giversState
            currentUserId = props.currentUserId
            canWrite = props.canWrite
            canFinance = props.canFinance
            onSearch = props.giversController::search
            onFilter = props.giversController::setFilter
            onPage = props.giversController::goToPage
            onRetry = props.giversController::load
            onAdd = props.onOpenDialog
            onImport = props.onOpenImport
            onDownloadTemplate = ::downloadGiverCsvTemplate
            onOpen = { giver ->
                window.location.hash = entityRouteHash(AppRoute.Givers, props.orgId, giver.id)
            }
        }
    }

    selected?.let { giver ->
        GiverDrawer {
            userId = props.currentUserId
            orgId = props.orgId
            key = giver.id.unsafeCast<react.Key>()
            summary = giver.copy(stewardName = props.members.firstOrNull { it.userId == props.detailState.giver?.stewardUserId }?.displayName ?: giver.stewardName)
            modalBlocked = props.dialogOpen || reminderOpen
            experienceController = props.experienceController
            experienceState = props.experienceState
            canFinance = props.canFinance
            onReminder = {
                props.reminderController.start()
                props.reminderController.selectSubject(ReminderSubjectOption("giver", giver.id, props.detailState.giver?.displayName ?: giver.displayName, "Giver"))
                setReminderOpen(true)
            }
            state = props.detailState
            controller = props.detailController
            canWrite = props.canWrite
            onEdit = {
                props.detailState.giver?.let { giver ->
                    props.onOpenDialog()
                    props.newGiverController.startEdit(giver)
                }
            }
            onClose = { props.experienceController.close(); setSelected(null); window.location.hash = com.dotdrives.web.workspaceHash(AppRoute.Givers.hash, props.orgId) }
        }
    }

    if (reminderOpen) {
        NextStepsStyles()
        NewReminderDialog {
            controller = props.reminderController; state = props.reminderState; members = props.members
            currentUserId = props.currentUserId; currentUserName = props.currentUserName; now = Clock.System.now()
            onCancel = { setReminderOpen(false) }
        }
    }
    if (props.dialogOpen) {
        NewGiverDialog {
            controller = props.newGiverController
            state = props.newGiverState
            currentUserId = props.currentUserId
            currentUserName = props.currentUserName
            members = props.members
            onCancel = props.onCloseDialog
        }
    }

    if (props.importOpen) {
        ImportWizard {
            controller = props.giversController
            onClose = props.onCloseImport
            onImported = { count ->
                setImportedCount(count)
                props.giversController.load()
                props.onCloseImport()
            }
        }
    }
}
